// Implements: F-UIX-018, F-USG-008
/**
 * The reads and writes of "Distribución" (UIX.6), cached per organization (checklist 14).
 *
 * Every key carries the organization's id, and a write puts the view it answers back under the
 * key of the organization it was SENT for: an answer can only ever be read back on the screen of
 * the organization it belongs to, however late it arrives or whichever organization is on screen.
 *
 * The people and teams lists name the rows; they are asked only once the server answered the
 * organization's whole view (its admin), never for a team lead, who may not list them.
 *
 * Two writes in flight keep the order they were sent in (a late answer of an older write is not
 * put over a newer one), and every successful write reads the policy again.
 *
 * Writes are plain calls sent once (`retry: false` in the client, checklist 13); each answers an
 * outcome the row shows, a refusal with the server's own sentence, never a silent retry.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useCallback, useRef } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import type {
  PeopleListView,
  PersonLimitInput,
  TeamAllocationInput,
  UsagePolicyView,
  WriteOutcome,
} from "../../../types/usagePolicy";
import { fetchOrganizationTeams } from "../api/scopeReportsApi";
import type { TeamListView } from "../api/scopeReportsApi";
import {
  deletePersonLimit,
  deleteTeamAllocation,
  fetchOrganizationPeople,
  fetchUsagePolicy,
  putPersonLimit,
  putTeamAllocation,
} from "../api/usagePolicyApi";

export function usagePolicyKey(organizationId: string): readonly string[] {
  return ["backoffice", "organization", organizationId, "policy"];
}

/**
 * The organization's teams: the very key "Consumo por ámbito" names its team rows under
 * (`organizationTeamsKey`), so both sections share one cached list.
 */
export function distributionTeamsKey(organizationId: string): readonly string[] {
  return ["backoffice", "organization", organizationId, "teams"];
}

export function organizationPeopleKey(organizationId: string): readonly string[] {
  return ["backoffice", "organization", organizationId, "people"];
}

export interface DistributionReads {
  readonly policy: UseQueryResult<UsagePolicyView, ApiError>;
  /** The organization's people, to name person rows; asked only for the whole view. */
  readonly people: UseQueryResult<PeopleListView, ApiError>;
  /** The organization's teams, to name team rows; asked only for the whole view. */
  readonly teams: UseQueryResult<TeamListView, ApiError>;
}

export function useDistribution(organizationId: string): DistributionReads {
  const policy = useQuery<UsagePolicyView, ApiError>({
    queryKey: usagePolicyKey(organizationId),
    queryFn: ({ signal }) => fetchUsagePolicy(organizationId, signal),
  });
  const whole = policy.data?.view === "organization";
  const people = useQuery<PeopleListView, ApiError>({
    queryKey: organizationPeopleKey(organizationId),
    queryFn: ({ signal }) => fetchOrganizationPeople(organizationId, signal),
    enabled: whole,
  });
  const teams = useQuery<TeamListView, ApiError>({
    queryKey: distributionTeamsKey(organizationId),
    queryFn: ({ signal }) => fetchOrganizationTeams(organizationId, signal),
    enabled: whole,
  });
  return { policy, people, teams };
}

export interface DistributionWrites {
  readonly setPersonLimit: (personId: string, input: PersonLimitInput) => Promise<WriteOutcome>;
  readonly clearPersonLimit: (personId: string) => Promise<WriteOutcome>;
  readonly setTeamAllocation: (teamId: string, input: TeamAllocationInput) => Promise<WriteOutcome>;
  readonly clearTeamAllocation: (teamId: string) => Promise<WriteOutcome>;
}

export function useDistributionWrites(organizationId: string): DistributionWrites {
  const queryClient = useQueryClient();
  // The order writes were SENT in: an answer is put in the cache only if no later write's answer
  // is there already, so two writes in flight resolving out of order never leave the older view.
  const issued = useRef(0);
  const applied = useRef(0);
  const write = useCallback(
    async (send: () => Promise<UsagePolicyView>): Promise<WriteOutcome> => {
      issued.current += 1;
      const order = issued.current;
      try {
        const view = await send();
        if (order > applied.current) {
          applied.current = order;
          queryClient.setQueryData(usagePolicyKey(organizationId), view);
        }
        // Then read the policy again: the server's state is the last word, whatever the order.
        void queryClient.invalidateQueries({ queryKey: usagePolicyKey(organizationId) });
        return { ok: true };
      } catch (error) {
        return { ok: false, error: asApiError(error) };
      }
    },
    [queryClient, organizationId],
  );
  return {
    setPersonLimit: (personId, input) => write(() => putPersonLimit(organizationId, personId, input)),
    clearPersonLimit: (personId) => write(() => deletePersonLimit(organizationId, personId)),
    setTeamAllocation: (teamId, input) => write(() => putTeamAllocation(organizationId, teamId, input)),
    clearTeamAllocation: (teamId) => write(() => deleteTeamAllocation(organizationId, teamId)),
  };
}
